import '../SettingsPage.scss';
import { Button, Checkbox, Icon, Input } from '@design-system/index';
import { ArchiveFilled, ArchiveLine, ModifierLine, ModifierFull, UnarchiveFilled, UnarchiveLine } from '@icons';
import { MutationTable } from '@components/Table/MutationTable/MutationTable';
import {
    OrganizationCode,
    OrganizationCodeToCreate,
    OrganizationCodeToUpdate
} from '@services/member/list/organization-codes/organization-codes.types';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { useOrganizationCodesPageVM } from './useOrganizationCodesPageVM';
import { Formik } from 'formik';

export default function OrganizationCodesPage() {
    const {
        mutationHook,
        isArchived,
        handleMetadata,
        handleArchiveOrganizationCodes,
        needReload,
        setIsArchived,
        MySwal,
        updateOrganizationCode,
        createOrganizationCode
    } = useOrganizationCodesPageVM();
    const columnHelper = createColumnHelper<OrganizationCode>();
    const navigate = useNavigate();

    const handleModificationOrganizationCode = async (organizationCode: OrganizationCodeToCreate | null, id?: number) => {
        await MySwal.fire<OrganizationCodeToUpdate>({
            title: organizationCode ? "Modification d'un code organisation" : "Création d'un code organisation",
            showCancelButton: true,
            confirmButtonColor: '#01539D',
            confirmButtonText: organizationCode ? 'Modifier' : 'Créer',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler',
            html: (
                <Formik
                    initialValues={{ code: organizationCode?.code ?? '', label: organizationCode?.label ?? '' }}
                    onSubmit={(values) => {
                        if (id) {
                            updateOrganizationCode({
                                id: id,
                                code: values.code,
                                label: values.label
                            });
                        } else {
                            createOrganizationCode({
                                code: values.code,
                                label: values.label
                            });
                        }
                        MySwal.close();
                    }}
                >
                    {({ values, handleChange, handleSubmit }) => (
                        <form id="form-organization-code" onSubmit={handleSubmit}>
                            <Input name="code" required label="Code" placeholder="Code" value={values.code} onChange={handleChange} />
                            <Input name="label" required label="Libellé" placeholder="Libellé" value={values.label} onChange={handleChange} />
                        </form>
                    )}
                </Formik>
            ),
            focusConfirm: false,
            preConfirm: () => {
                const form = document.getElementById('form-organization-code') as HTMLFormElement;
                form.requestSubmit();
                return false;
            }
        });
    };

    const columns = [
        columnHelper.display({
            id: 'select',
            size: 0,
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label={`Sélectionner ligne ${row.id}`}
                />
            )
        }),
        columnHelper.accessor('code', {
            header: 'Code',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('label', {
            header: 'Libellé',
            enableSorting: true,
            cell: (info) => {
                return <>{info.getValue()}</>;
            }
        }),
        columnHelper.accessor('id', {
            header: 'Actions',
            cell: (info) => {
                return (
                    <div className="settings-actions">
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={<ModifierFull />} size={20} color="primary" />}
                            icon={<Icon icon={<ModifierLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleModificationOrganizationCode(
                                    { code: info.row.original.code, label: info.row.original.label },
                                    info.row.original.id
                                );
                            }}
                        />
                        <Button
                            variant="icon"
                            color="primary"
                            size="small"
                            hoverIcon={<Icon icon={isArchived ? <UnarchiveFilled /> : <ArchiveFilled />} size={20} color="primary" />}
                            icon={<Icon icon={isArchived ? <UnarchiveLine /> : <ArchiveLine />} size={20} color="primary" />}
                            onClick={() => {
                                handleArchiveOrganizationCodes([info.row.original], !isArchived);
                            }}
                        />
                    </div>
                );
            }
        })
    ] as ColumnDef<OrganizationCode>[];

    return (
        <>
            <div className="settings-tabs-content organization-codes">
                <MutationTable
                    noHeader
                    key={isArchived ? 'archived' : 'active'}
                    columns={columns}
                    mutationHook={mutationHook}
                    onRowClick={(row) => navigate(`/members-management/settings/organization-codes/${row.id}`)}
                    defaultSorting={{
                        field: 'code',
                        direction: 'desc'
                    }}
                    onMetadata={handleMetadata}
                    needReload={needReload}
                    createButtonOnClick={() => handleModificationOrganizationCode(null)}
                    createButtonLabel="Créer un code"
                    archiveButtonOnClick={() => setIsArchived(!isArchived)}
                    archiveButtonLabel={isArchived ? 'Actifs' : 'Archivés'}
                    deleting={{
                        enabled: isArchived,
                        entity: 'organization_code',
                    }}
                    footer={{
                        actions: [
                            {
                                label: isArchived ? 'Désarchiver' : 'Archiver',
                                icon: isArchived ? <UnarchiveLine /> : <ArchiveLine />,
                                hoverIcon: isArchived ? <UnarchiveFilled /> : <ArchiveFilled />,
                                onClick: (lines: OrganizationCode[]) => handleArchiveOrganizationCodes(lines, !isArchived)
                            }
                        ]
                    }}
                />
            </div>
        </>
    );
}
